import { Dispatch, FC, SetStateAction, useState, useEffect, useCallback } from 'react'
import { Container, Drawer } from 'rsuite'
import MenuIcon from '@rsuite/icons/Menu'
import ArrowBackIcon from '@rsuite/icons/ArowBack'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import omit from 'lodash/omit'

import MobileDrawerHeader from '../MobileUI/MobileDrawerHeader'
import SearchBar from '../../../Search/SearchWrapper'
import { capitalizeFirstLetter } from '../../../../utils/util'
import { getFilterQueryIds } from '../../../Catalog/Pickers/util'
import useUrls from '../../../../services/useUrls'
import useCategoryDataStructure from '../../../hooks/useCategoryDataStructure'
import { useApi } from '../../../../services/useApi'
import { QueryParam, ResourceTag } from '../../../../utils/constants'
import { Catalog } from '../../../../utils/types/Product'
import MainCategoriesMenu from './MainCategoriesMenu'
import SubCategoriesMenu from './SubCategoriesMenu'

import headerStyles from '../../../../styles/Header.module.less'

interface MobileCategoriesMenuProps {
  isDrawerOpen: boolean
  isInSearchMode: boolean
  toggleIsDrawerOpen: () => void
  setIsInSearchMode: Dispatch<SetStateAction<boolean>>
}

const MobileCategoriesMenu: FC<MobileCategoriesMenuProps> = (props) => {
  const {
    isDrawerOpen: isMobileCategoriesDrawerOpen,
    isInSearchMode,
    toggleIsDrawerOpen: toggleMobileCategoriesDrawer,
    setIsInSearchMode,
  } = props

  const [breadcrumb, setBreadcrumb] = useState<Catalog[]>([])
  const [animationDirection, setAnimationDirection] = useState<'forward' | 'backward' | null>(null)

  const selectedTopCategoryCode = breadcrumb.at(0)?.['catalog.code'] || ''
  const navigationLevel = breadcrumb.length > 0 ? 'subcategory' : 'main'

  const { t } = useTranslation('products')
  const { getResourceCount } = useApi()
  const { pushT } = useUrls()
  const { query } = useRouter()
  const {
    subCategory,
    categoriesLoading,
    isSubCategoriesLoading,
  } = useCategoryDataStructure(selectedTopCategoryCode)

  const activeCategory = breadcrumb.at(-1) || subCategory?.[0] || {}
  const searchQuery = query[QueryParam.search]?.toString()
  const countQuery = searchQuery ? 'aggregate=index.catalog.id' : 'aggregate=index.catalog.id'
  const { data: productsAggregate } = getResourceCount(ResourceTag.product, countQuery)

  const selectedSubCategory = subCategory?.[0] || {}

  // Handle animation completion
  useEffect(() => {
    if (animationDirection) {
      const timer = setTimeout(() => {
        setAnimationDirection(null)
      }, 300)
      return () => {
        clearTimeout(timer)
        return undefined
      }
    }
    return undefined
  }, [animationDirection])

  const handleSubCategoryClick = useCallback((categoryCode: string) => {
    // This could be either final selection or further subcategory navigation
    const currentQueries = omit(query, getFilterQueryIds(query), QueryParam.search)
    const urlWithCategory = selectedTopCategoryCode !== categoryCode
      ? `/products/category/${selectedTopCategoryCode}/${categoryCode}`
      : `/products/category/${categoryCode}`

    pushT(
      {
        pathname: urlWithCategory,
        query: currentQueries,
      },
      undefined,
      { shallow: true, scroll: true },
    )

    // Close the drawer after selection
    toggleMobileCategoriesDrawer()
  }, [query, selectedTopCategoryCode])

  const handleBackNavigation = () => {
    setAnimationDirection('backward')

    if (navigationLevel === 'subcategory' && breadcrumb.length === 1) {
      setBreadcrumb([])
    } else {
      setBreadcrumb(breadcrumb.slice(0, -1))
    }
  }

  const handleSearchModeBackClick = () => setIsInSearchMode(false)

  const getHeaderTitle = () => {
    if (isInSearchMode) return capitalizeFirstLetter(t('url:search'))
    if (navigationLevel === 'main') return t('Categories')
    return activeCategory?.['catalog.label'] || ''
  }

  const getBackHandler = () => {
    if (isInSearchMode) return handleSearchModeBackClick
    if (navigationLevel !== 'main') return handleBackNavigation
    return undefined
  }

  const renderCurrentLevel = () => {
    // During animation, we show both top level and subcategories with animation classes
    if (animationDirection) {
      return (
        <>
          {breadcrumb.length === 0 && (
            <div className={navigationLevel === 'main' ? headerStyles['slide-in-left'] : headerStyles['slide-out-left']}>
              <MainCategoriesMenu
                productsAggregate={productsAggregate}
                setAnimationDirection={setAnimationDirection}
                setBreadcrumb={setBreadcrumb}
              />
            </div>
          )}
          {breadcrumb.length > 0 && (
            <div className={animationDirection === 'backward' ? headerStyles['slide-out-right'] : headerStyles['slide-in-right']}>
              <SubCategoriesMenu
                handleSubCategoryClick={handleSubCategoryClick}
                selectedSubCategory={selectedSubCategory}
                categoriesLoading={categoriesLoading}
                isSubCategoriesLoading={isSubCategoriesLoading}
                setBreadcrumb={setBreadcrumb}
                breadcrumb={breadcrumb}
                setAnimationDirection={setAnimationDirection}
              />
            </div>
          )}
        </>
      )
    }

    // When not animating, only show the current level
    switch (navigationLevel) {
      case 'main':
        return (
          <MainCategoriesMenu
            productsAggregate={productsAggregate}
            setAnimationDirection={setAnimationDirection}
            setBreadcrumb={setBreadcrumb}
          />
        )
      case 'subcategory':
        return (
          <SubCategoriesMenu
            handleSubCategoryClick={handleSubCategoryClick}
            selectedSubCategory={selectedSubCategory}
            categoriesLoading={categoriesLoading}
            isSubCategoriesLoading={isSubCategoriesLoading}
            setBreadcrumb={setBreadcrumb}
            breadcrumb={breadcrumb}
            setAnimationDirection={setAnimationDirection}
          />
        )
      default:
        return (
          <MainCategoriesMenu
            productsAggregate={productsAggregate}
            setAnimationDirection={setAnimationDirection}
            setBreadcrumb={setBreadcrumb}
          />
        )
    }
  }

  return (
    <>
      <MenuIcon
        className="icon-size-double"
        onClick={toggleMobileCategoriesDrawer}
      />
      <Drawer
        placement="left"
        open={isMobileCategoriesDrawerOpen}
        onClose={toggleMobileCategoriesDrawer}
        onExited={() => {
          setIsInSearchMode(false)
          setBreadcrumb([])
        }}
        className={headerStyles['mobile-categories-menu']}
        size="full"
      >
        {!isInSearchMode && (
          <MobileDrawerHeader
            handleBackNavigation={getBackHandler()}
          >
            {getHeaderTitle()}
          </MobileDrawerHeader>
        )}
        <Drawer.Body className="padding-spacer-double">
          {isInSearchMode ? (
            <ArrowBackIcon
              role="button"
              className={headerStyles['back-icon']}
              onClick={() => {
                toggleMobileCategoriesDrawer()
              }}
            />
          ) : null}
          <Container onClick={() => setIsInSearchMode(true)}>
            <SearchBar
              externalSearchCallback={toggleMobileCategoriesDrawer}
              graphicRenderCondition="focus"
              className={headerStyles['megamenu-search']}
              isLargeInput
              collapsed={!isInSearchMode}
            />
          </Container>
          {!isInSearchMode && (
            <div className={`margin-top-spacer-tripple ${headerStyles['mobile-categories-menu-items-container']}`}>
              {renderCurrentLevel()}
            </div>
          )}
        </Drawer.Body>
      </Drawer>
    </>
  )
}

export default MobileCategoriesMenu
